import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { actorInfo } from '../mock/komalData';
import { Mail, Phone, MapPin, Send, Film, ExternalLink, Camera } from 'lucide-react';
import LetterboxdIcon from './LetterboxdIcon';
import ImdbIcon from './ImdbIcon';
import { toast } from '../hooks/use-toast';

const ContactKomal = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Message Sent!",
          description: "Thank you for reaching out. I'll get back to you soon!",
        });
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact" className="min-h-0 lg:min-h-screen bg-[#1a1a1a] py-12 lg:py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header - Consistent with other sections */}
        <div className="text-center mb-10 lg:mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-3 lg:mb-4">
            LET'S <span className="text-[#FF6B6B]">CONNECT</span>
          </h2>
          <div className="w-20 lg:w-24 h-1 bg-[#FF6B6B] mx-auto mb-4 lg:mb-6"></div>
          <p className="text-gray-400 text-sm lg:text-lg max-w-2xl mx-auto leading-relaxed">
            Ready to collaborate? Let's bring powerful stories to life together.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6 lg:space-y-8">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 lg:mb-6">Get In Touch</h3>
              <p className="text-gray-300 text-sm lg:text-lg mb-6 lg:mb-8 leading-relaxed">
                I'm currently based in Mumbai and available for auditions, meetings, and collaborations. 
                Feel free to reach out for potential projects or representation opportunities.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-4 lg:space-y-6">
              <div className="flex items-start gap-3 lg:gap-4 group">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#FF6B6B]/20 transition-colors">
                  <Mail className="text-[#FF6B6B]" size={20} />
                </div>
                <div>
                  <div className="text-gray-400 text-xs lg:text-sm mb-1">Email</div>
                  <a
                    href={`mailto:${actorInfo.email}`}
                    className="text-white text-base lg:text-lg hover:text-[#FF6B6B] transition-colors break-all"
                  >
                    {actorInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 lg:gap-4 group">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#4ECDC4]/20 transition-colors">
                  <Phone className="text-[#4ECDC4]" size={20} />
                </div>
                <div>
                  <div className="text-gray-400 text-xs lg:text-sm mb-1">Phone</div>
                  <a
                    href={`tel:${actorInfo.phone}`}
                    className="text-white text-base lg:text-lg hover:text-[#4ECDC4] transition-colors"
                  >
                    {actorInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 lg:gap-4 group">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-[#FFD700]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#FFD700]/20 transition-colors">
                  <MapPin className="text-[#FFD700]" size={20} />
                </div>
                <div>
                  <div className="text-gray-400 text-xs lg:text-sm mb-1">Location</div>
                  <div className="text-white text-base lg:text-lg">{actorInfo.location}, India</div>
                  <div className="text-gray-500 text-xs lg:text-sm">Native: {actorInfo.native}</div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="pt-4 lg:pt-8">
              <h4 className="text-lg lg:text-xl font-bold text-white mb-3 lg:mb-4">Quick Access</h4>
              <div className="flex flex-col gap-2 lg:gap-3">
                <a
                  href={actorInfo.showreelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-gray-300 hover:text-[#FF6B6B] transition-colors group text-sm lg:text-base"
                >
                  <Film size={18} className="group-hover:scale-110 transition-transform" />
                  <span>View Showreel</span>
                  <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-3 text-gray-300 hover:text-[#FFD700] transition-colors group text-sm lg:text-base"
                  data-testid="contact-portfolio-link"
                >
                  <Camera size={18} className="group-hover:scale-110 transition-transform" />
                  <span>View Portfolio</span>
                </Link>
                {actorInfo.imdb && (
                  <a
                    href={actorInfo.imdb}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-imdb-link"
                    className="inline-flex items-center gap-3 text-gray-300 hover:text-[#F5C518] transition-colors group text-sm lg:text-base"
                  >
                    <ImdbIcon size={18} className="group-hover:scale-110 transition-transform" />
                    <span>View IMDb Profile</span>
                    <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                )}
                {actorInfo.letterboxd && (
                  <a
                    href={actorInfo.letterboxd}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-letterboxd-link"
                    className="inline-flex items-center gap-3 text-gray-300 hover:text-[#00E054] transition-colors group text-sm lg:text-base"
                  >
                    <LetterboxdIcon size={18} className="group-hover:scale-110 transition-transform" />
                    <span>Find me on Letterboxd</span>
                    <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                )}
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="text-lg lg:text-xl font-bold text-white mb-3 lg:mb-4">Follow Me</h3>
              <a
                href={actorInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-gray-300 hover:text-[#E1306C] transition-colors group"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-[#405DE6] via-[#E1306C] to-[#FFDC80] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs lg:text-sm text-gray-500">Instagram</div>
                  <div className="font-medium text-sm lg:text-base">@komalunveiled</div>
                </div>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#0a0a0a] p-6 lg:p-8 rounded-lg border border-gray-800">
            <h3 className="text-xl lg:text-2xl font-bold text-white mb-4 lg:mb-6">Send a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-300 mb-2 font-medium text-sm lg:text-base">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white text-sm lg:text-base focus:outline-none focus:border-[#FF6B6B] transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-300 mb-2 font-medium text-sm lg:text-base">
                  Your Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white text-sm lg:text-base focus:outline-none focus:border-[#FF6B6B] transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-300 mb-2 font-medium text-sm lg:text-base">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white text-sm lg:text-base focus:outline-none focus:border-[#FF6B6B] transition-colors"
                  placeholder="+91-XXXXXXXXXX"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-gray-300 mb-2 font-medium text-sm lg:text-base">
                  Company/Production House
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white text-sm lg:text-base focus:outline-none focus:border-[#FF6B6B] transition-colors"
                  placeholder="Company name (optional)"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-300 mb-2 font-medium text-sm lg:text-base">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white text-sm lg:text-base focus:outline-none focus:border-[#FF6B6B] transition-colors resize-none"
                  placeholder="Tell me about your project..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full px-6 lg:px-8 py-3 lg:py-4 bg-[#FF6B6B] text-white font-bold rounded-lg hover:bg-[#FF8E53] transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-[#FF6B6B]/50 flex items-center justify-center gap-2 lg:gap-3 text-sm lg:text-base"
              >
                <Send size={18} />
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactKomal;
